// **
// * /!\ POLYFILLS MUST BE AT THE TOP-LEVEL
// **
import '../lib/polyfills'
import {captureConnectAsSessionId, installConnectAsLinkPropagation} from '@ambler/shared-front'
// **
// * /!\ POLYFILLS MUST BE AT THE TOP-LEVEL
// **

import React from 'react'
import {isStaffEmail} from '@ambler/shared'
import {gql} from '@apollo/client'
import Head from 'next/head'
import {Baseline, Loader} from '@ambler/andive-next/deprecated'
import styled, {createGlobalStyle} from 'styled-components'
import type {FixType} from '@ambler/shared'
import {useRouter} from 'next/router'
import {Andiv, palette, Text, Toaster} from '@ambler/andive-next'
import {AuthSessionProvider, useAuth, useMainMt} from '../components/stack/auth'
import {useProduct} from '../components/stack/product'
import '../lib/pdfjs-init'
import {ResponsiveProvider} from '../components/stack/responsive'
import {ReactDatesAmblerTheme, ReactDatesDatePickerCss} from '../components/datetime-filter'
import {withAsideLoader} from '../components/aside-loader'
import {ErrorCard} from '../components/error'
import {FaviconProvider} from '../components/favicon'
import {Updater} from '../components/app-updater'
import Sentry from '../lib/sentry'
import {PUBLIC_PATHS, SIGNED_IN_MARKER, SIGNED_OUT_PATH, isAuthError, withoutSignInMarker} from '../lib/auth/session'
import {CanGoBackProvider} from '../components/stack/router'
import {withApollo} from '../components/stack/apollo/with-apollo'
import {useMutation} from '../hooks/use-mutation'
import {getUpdateDevicePayload, useForegroundNotifications} from '../lib/firebase-messaging'
import type {
  BO_UpdatePushNotificationsDeviceMutation_,
  BO_UpdatePushNotificationsDeviceMutationVariables,
} from './_app.generated'
import {useProfile, withProfile} from '../components/stack/profile'

// Module scope so it runs before the first render, hence before any request can
// read the session: takes the impersonation id out of the URL into this tab's
// memory, then keeps carrying it into tabs opened from here.
captureConnectAsSessionId()
installConnectAsLinkPropagation()

const updatePushNotificationsDeviceMutation = gql`
  mutation BO_UpdatePushNotificationsDeviceMutation($data: PushNotificationsUpsertDeviceDataInput!) {
    pushNotificationsUpsertDevice(data: $data)
  }
`

const BoBaseline = createGlobalStyle`
  body {
    background: ${palette.amblea.grey[100]};
  }
`

const GlowingBorder = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  border: 8px solid rgba(229, 181, 135, 0.8);
  z-index: 99999;

  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  pointer-events: none;
`
const ConnectAsOverlay = ({asName}: {asName: string}) => {
  return (
    <GlowingBorder>
      <Andiv p="8px" borderRadius="4px 0 0 0" bg="rgba(229, 181, 135, 0.8)">
        <Text t="body2">
          Connecté en tant que : <b>{asName}</b>
        </Text>
      </Andiv>
    </GlowingBorder>
  )
}

const FirebaseProvider: React.FC = ({children}) => {
  const [deviceInitialized, setDeviceInitialized] = React.useState(false)
  const {isAuthenticated} = useAuth()

  const [updateDevice] = useMutation<
    BO_UpdatePushNotificationsDeviceMutation_,
    BO_UpdatePushNotificationsDeviceMutationVariables
  >({
    mutation: updatePushNotificationsDeviceMutation,
  })

  React.useEffect(() => {
    const initDevice = async () => {
      await updateDevice({
        variables: {
          data: await getUpdateDevicePayload(),
        },
      })
    }
    if (isAuthenticated && !deviceInitialized) {
      initDevice()
      setDeviceInitialized(true)
    }
  }, [isAuthenticated, updateDevice, deviceInitialized])

  useForegroundNotifications()

  return <>{children}</>
}

const AuthenticatedApp_: React.FC = ({children}) => {
  const [loading, setLoading] = React.useState(true)
  const router = useRouter()
  const product = useProduct()
  const mainMt = useMainMt()
  const profile = useProfile()
  const {
    user,
    mtAcls,
    isAuthenticated,
    isSessionLost,
    isEmailConfirmed,
    isLoading,
    signIn,
    signOut,
    isConnectAs,
    identityError,
  } = useAuth()

  const isErrorPage = router.pathname === '/auth/error'
  const isPublicPage = PUBLIC_PATHS.includes(router.pathname)
  // A login that could not complete comes back on the page it was started
  // from, with the reason in the query.
  const authError = router.query['error']

  React.useEffect(() => {
    if (isAuthenticated) {
      Sentry.setUser({
        id: user.id,
        name: user.name,
        email: user.email,
        extra: {
          isEmailConfirmed,
          role: user.role,
          mtAcls: JSON.stringify(mtAcls, null, 2), // if we don't do this, Sentry will not show the nested object
        },
      })
    }
  }, [user, isAuthenticated, isEmailConfirmed, mtAcls])

  React.useEffect(() => {
    if (!router.isReady) {
      return
    }
    if (isLoading) {
      return
    }
    if (isAuthError(authError) && !isErrorPage) {
      router.replace(`/auth/error?error=${authError}`)
      return
    }
    if (isPublicPage) {
      // ? we don't want those pages to be auth protected.
      setLoading(false)
      return
    }
    if (identityError) {
      // API unreachable or 5xx: no sign-in, no check on an unknown identity.
      // Shown in place: /auth/error would send an authenticated user back here.
      setLoading(false)
      return
    }
    if (!isAuthenticated) {
      if (isSessionLost) {
        // The provider's own session would complete a sign-in here, undoing
        // the sign-out.
        router.replace(SIGNED_OUT_PATH)
        return
      }
      if (router.query[SIGNED_IN_MARKER]) {
        router.replace('/auth/error?error=session_not_kept')
        return
      }
      // Opening the back-office is already the intent to sign in.
      signIn({returnTo: router.asPath})
      return
    } else {
      const unmarked = withoutSignInMarker(router.asPath)
      if (unmarked !== undefined) {
        // The marker has done its job; left in the address, it would travel with
        // a copied link and send its reader to the error page.
        router.replace(unmarked, undefined, {shallow: true})
        return
      }
      if (!isEmailConfirmed) {
        router.replace(`/auth/not_verified`)
        return
      }
      if (product === 'A4D') {
        if (!mainMt || mainMt.mt.type !== 'DISPATCHER') {
          router.replace('/')
          return
        }
      } else {
        // * BO
        if (mainMt && mainMt.mt.type === 'DISPATCHER') {
          router.replace('/a4d/dashboard')
          return
        }
        if (!isStaffEmail(user?.email)) {
          signOut()
          return
        }
      }
    }
    setLoading(false)
  }, [
    authError,
    identityError,
    isErrorPage,
    isPublicPage,
    isAuthenticated,
    isSessionLost,
    isLoading,
    mainMt,
    product,
    router,
    signIn,
    signOut,
    user,
    isEmailConfirmed,
  ])

  if (loading) {
    return <Loader />
  }

  if (identityError) {
    return (
      <ErrorCard
        error={{
          message:
            "Votre profil n'a pas pu être chargé. Le service est peut-être momentanément indisponible : rechargez la page dans quelques instants.",
        }}
      />
    )
  }

  return (
    <>
      {isConnectAs && <ConnectAsOverlay asName={profile?.email ?? '...'} />}
      {children}
    </>
  )
}

const AuthenticatedApp = withProfile(AuthenticatedApp_)

function BoApp({Component, pageProps, err}: FixType) {
  // injected err is a workaround for https://github.com/vercel/next.js/issues/8592
  return (
    <>
      <Head>
        {/*
          Empty title serve as a fallback here,
          When no title is define in the subsequent component tree
        */}
        <title />
        <link rel="manifest" href="/static/manifest.json" />
        <meta name="referrer" content="same-origin" />
        <link rel="icon" href="/static/favicon-64x64.png" type="image/x-png" />
      </Head>
      <ResponsiveProvider>
        <CanGoBackProvider>
          <FaviconProvider>
            <BoBaseline />
            <Baseline />
            <>
              <Toaster />
              <Updater />
              <AuthSessionProvider>
                <AuthenticatedApp>
                  <FirebaseProvider>
                    <Component {...pageProps} err={err} />
                  </FirebaseProvider>
                </AuthenticatedApp>
              </AuthSessionProvider>
            </>
            <ReactDatesAmblerTheme />
            <ReactDatesDatePickerCss />
          </FaviconProvider>
        </CanGoBackProvider>
      </ResponsiveProvider>
    </>
  )
}

export default withAsideLoader(withApollo(BoApp))
